import { useCallback, useEffect, useState } from "react";
import { canvasApi } from "../api/client";
import PublishDialog from "./PublishDialog";

/**
 * Frames the canvas preview origin.
 *
 * Two independent isolations apply, and the sandbox attribute is the one this
 * component owns: `allow-scripts` WITHOUT `allow-same-origin` forces an opaque
 * origin, so the previewed page cannot reach chatter's DOM or cookies even if
 * the preview host were ever misconfigured onto the app's own domain.
 */
export default function CanvasPreview({
  workspaceId,
  revisionId,
  dirty,
  onError,
}: {
  workspaceId: string;
  revisionId: string | null;
  dirty: boolean;
  onError(message: string): void;
}) {
  const [url, setUrl] = useState("");
  const [nonce, setNonce] = useState(0);
  const [opening, setOpening] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const mint = useCallback(async () => {
    if (!revisionId) return setUrl("");
    try {
      setUrl((await canvasApi.previewToken(workspaceId, revisionId)).url);
    } catch (err) {
      onError((err as Error).message);
    }
  }, [workspaceId, revisionId, onError]);

  // Tokens are short-lived and scoped to one revision, so a fresh one is
  // minted whenever the previewed revision changes or the user reloads.
  useEffect(() => {
    void mint();
  }, [mint, nonce]);

  const openInNewTab = useCallback(async () => {
    if (!revisionId) return;
    setOpening(true);
    try {
      // Mint a fresh token rather than reusing `url` — the iframe's token may
      // be close to its 15-minute TTL by the time the user clicks.
      const fresh = await canvasApi.previewToken(workspaceId, revisionId);
      window.open(fresh.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setOpening(false);
    }
  }, [workspaceId, revisionId, onError]);

  if (!revisionId) {
    return <p className="canvas-empty">Save a revision to preview it.</p>;
  }

  return (
    <div className="canvas-preview">
      <div className="canvas-preview-bar">
        {dirty ? (
          <span className="canvas-preview-stale">
            Unsaved changes — the preview shows the last saved revision.
          </span>
        ) : (
          <span className="canvas-preview-ok">Previewing the current revision.</span>
        )}
        <button type="button" className="btn btn-sm" onClick={() => setNonce(n => n + 1)}>
          Reload
        </button>
        <button type="button" className="btn btn-sm" onClick={() => void openInNewTab()} disabled={opening}>
          Preview in New Tab
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => setPublishing(true)}
          title={dirty ? "Publishes the last saved revision — save first to include your edits" : "Publish this revision to here.now"}
        >
          Publish…
        </button>
      </div>
      {publishing && (
        <PublishDialog workspaceId={workspaceId} revisionId={revisionId} onClose={() => setPublishing(false)} />
      )}
      {url ? (
        <iframe
          key={url}
          title="Canvas preview"
          src={url}
          sandbox="allow-scripts allow-forms allow-popups allow-modals"
          referrerPolicy="no-referrer"
        />
      ) : (
        <p className="canvas-empty">Loading preview…</p>
      )}
    </div>
  );
}
